'use client';

import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import Image, { type ImageLoaderProps } from 'next/image';

// Bypass @unpic/react's Supabase CDN auto-detection (which rewrites the URL
// to /storage/v1/render/image/public/...?width=N — that endpoint 403s when
// the Image Transformation add-on is disabled). Returning the src verbatim
// makes the shim render a plain <img>.
const passthroughLoader = ({ src }: ImageLoaderProps) => src;
import Link from 'next/link';
import { Icon } from '@iconify/react';
import { useFocusEffect } from '../../hooks';
import gsap from 'gsap';
import { TechnologyItem, TechnologyTagList } from './technology-tag';
import { useTranslation } from 'react-i18next';

/**
 * FeaturedProject component
 * Displays a featured project with image, title, description, and technologies
 * Used in the hero section of the projects page
 * Enhanced with animations and styling consistent with the rest of the portfolio
 */
export interface FeaturedProjectProps {
  title: string;
  description: string;
  imageSrc: string;
  href: string;
  iconName?: string;
  technologies?: TechnologyItem[];
  className?: string;
  translationKey?: string;
}

export function FeaturedProject({
  title,
  description,
  imageSrc,
  href,
  iconName,
  technologies = [],
  className,
  translationKey
}: FeaturedProjectProps) {
  const { t } = useTranslation();
  const [imageLoading, setImageLoading] = useState(true);

  // Use our custom focus effect hook
  const { isFocused, ref, focusProps } = useFocusEffect<HTMLDivElement>();

  // Map icon names to Iconify icon IDs
  const getIconId = (name: string) => {
    const iconMap: Record<string, string> = {
      'FaTree': 'fa6-solid:tree',
      'FaEye': 'fa6-solid:eye',
      'IoMdPulse': 'ion:pulse',
      'RiSpeakLine': 'ri:speak-line',
      'FaHeadphones': 'fa6-solid:headphones',
      'FaMicrophone': 'fa6-solid:microphone'
    };

    return iconMap[name] || 'carbon:document';
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tl = gsap.timeline();

    if (containerRef.current) gsap.set(containerRef.current, { opacity: 0, y: 20 });
    tl.to(containerRef.current, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" });

    if (headerRef.current) {
      tl.fromTo(headerRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2");
    }

    if (iconRef.current) {
      gsap.to(iconRef.current, { y: -5, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
    }

    if (contentRef.current) {
      gsap.fromTo(contentRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.2 });
    }
  }, []);

  return (
    <div
      ref={containerRef}
    >
      <div
        ref={headerRef}
        className="flex items-center gap-2 mb-4"
      >
        <div ref={iconRef}>
          <Icon
            icon="carbon:star-filled"
            className="h-5 w-5 text-slate-600 dark:text-slate-400"
          />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">{t('Projects.featured')}</h2>
        <div className="h-px flex-grow bg-slate-200 dark:bg-slate-700/50"></div>
      </div>

      <Link href={href} className="block group">
        <div
          ref={ref as any}
          className={cn(
            'relative flex flex-col md:flex-row overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700/50',
            'bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900/80 dark:to-slate-800/80 transition-all duration-300',
            'hover:border-slate-300 dark:hover:border-slate-600 hover:-translate-y-1.5',
            isFocused ? 'shadow-lg ring-2 ring-slate-300/50 dark:ring-slate-600/30 translate-y-[-4px]' : 'shadow-sm',
            className
          )}
          {...focusProps}
        >
          {/* Project Image */}
          <div className="relative h-64 md:h-auto md:w-1/2 overflow-hidden bg-slate-200/50 dark:bg-slate-800/50">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-slate-300/50 dark:border-slate-600/50 border-t-slate-500 dark:border-t-slate-400 rounded-full animate-spin" />
              </div>
            )}
            <Image
              src={imageSrc}
              alt={translationKey ? t(`Projects.items.${translationKey}.title`) : title}
              fill
              className={cn(
                "object-cover transition-all duration-500",
                imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300",
                isFocused ? "scale-105 blur-[2px]" : "scale-100"
              )}
              sizes="(max-width: 768px) 100vw, 50vw"
              onLoad={() => setImageLoading(false)}
              priority
              loader={passthroughLoader}
            />
            <div className={cn(
              "absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-800/50 to-transparent transition-opacity duration-300",
              isFocused ? "opacity-90" : "opacity-80"
            )} />

            {/* Icon Badge */}
            {iconName && (
              <div className={cn(
                "absolute top-4 left-4 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm p-3 rounded-full shadow-sm transition-transform duration-300",
                isFocused ? "scale-110" : "scale-100"
              )}>
                <Icon
                  icon={getIconId(iconName)}
                  className="h-6 w-6 text-slate-900 dark:text-slate-200"
                />
              </div>
            )}

            {/* Featured Badge */}
            <div className={cn(
              "absolute top-4 right-4 bg-slate-800/90 dark:bg-slate-700/90 text-white px-3 py-1 rounded-full text-xs font-medium transition-transform duration-300",
              isFocused ? "scale-110" : "scale-100"
            )}>
              {t('Projects.featured')}
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col flex-grow p-6 md:p-8 space-y-4 md:w-1/2">
            <h2 className={cn(
              "text-2xl md:text-3xl font-bold transition-transform duration-300 text-slate-800 dark:text-slate-200",
              isFocused ? "text-slate-900 dark:text-white translate-y-[-2px]" : ""
            )}>
              {translationKey ? t(`Projects.items.${translationKey}.title`) : title}
            </h2>

            <p className={cn(
              "text-slate-600 dark:text-slate-400 text-base md:text-lg flex-grow transition-colors duration-300",
              isFocused ? "text-slate-700 dark:text-slate-300" : ""
            )}>
              {translationKey ? t(`Projects.items.${translationKey}.description`) : description}
            </p>

            {/* Technologies */}
            {technologies.length > 0 && (
              <div
                ref={contentRef}
                className="mt-4"
              >
                <TechnologyTagList
                  technologies={technologies}
                  size="md"
                  variant="outlined"
                  className="gap-2"
                />
              </div>
            )}

            {/* Link-like Span */}
            <span
              className={cn(
                "inline-flex items-center gap-2 text-base font-medium mt-4 transition-all duration-300",
                isFocused
                  ? "text-slate-800 dark:text-slate-200 translate-x-1"
                  : "text-slate-700 dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-slate-200"
              )}
            >
              {t('Projects.viewProject')} <Icon
                icon="carbon:arrow-right"
                className={cn(
                  "h-4 w-4 transition-transform duration-300",
                  isFocused ? "translate-x-1" : "group-hover:translate-x-1"
                )}
              />
            </span>
          </div>

          {/* Focus overlay */}
          <div className={cn(
            "absolute inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 z-10",
            isFocused ? "opacity-100" : "opacity-0 pointer-events-none"
          )}>
            <span className="text-white font-medium px-6 py-3 rounded-full bg-slate-800/80 backdrop-blur-sm text-lg">
              {t('Projects.viewProject')}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
} 